import { HttpErrorResponse } from '@angular/common/http';
import { TeamRefusalCode } from './team.types';

/**
 * A team refusal, flat, as `app/asgi.py` sends it.
 *
 * A dict `detail` becomes the response body directly, so what arrives is
 * `{error, message, …}` — never `{detail: {…}}`. Every field but `error` is
 * per-code.
 */
export interface TeamRefusal {
	code: TeamRefusalCode;
	/** The server's own sentence. Preferred over ours wherever it says more. */
	message: string | null;
	/**
	 * WHICH INPUT CAUSED IT, when the refusal is about one — `email` on the two
	 * address conflicts, `rung_plan_id` on a tier mismatch. This is the field
	 * that lets the invite dialog report a refusal at the control that earned it
	 * rather than as a banner over a form whose other half was fine.
	 */
	field: string | null;
	/** Seconds to wait, on `invite_cooldown`. */
	retryAfter: number | null;
	/** `expired` / `revoked` / `accepted`, on `invite_not_acceptable`. */
	reason: string | null;
}

interface TeamRefusalBody {
	error?: string;
	message?: string;
	field?: string;
	retry_after?: number;
	reason?: string;
}

/**
 * The refusal in a failed `/team` request, or null when the failure was not a
 * stated one.
 *
 * Read BY CODE and never by status: `role_forbidden`, `seat_frozen` and
 * `feature_disabled` all answer 403, and the four conflicts all answer 409, so
 * the status alone separates nothing.
 */
export function parseTeamRefusal(error: unknown): TeamRefusal | null {
	if (!(error instanceof HttpErrorResponse)) {
		return null;
	}
	const body = error.error as TeamRefusalBody | null;
	const code = body?.error;
	const known = Object.values(TeamRefusalCode) as string[];
	if (!code || !known.includes(code)) {
		return null;
	}
	return {
		code: code as TeamRefusalCode,
		message: body?.message ?? null,
		field: body?.field ?? null,
		retryAfter: body?.retry_after ?? null,
		reason: body?.reason ?? null
	};
}
